import { UNSET_PROPERTY_KEY } from '@blaze-shared/elements-domain';
import { type AppExtendedThemeValue } from '@blaze-shared/theming-domain';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import {
  type MetadataMergeResult,
  previousSourceSymbol,
  sourceSymbol,
  themeValueKeySymbol,
  unsetDetectedSymbol,
} from './merge-metadata';
import { determineMergeType, MergeType } from './merge-type';
import { type PropsSource, type SourceType } from './props-source';

export function getKeysToMerge(sources: PropsSource[]): Set<string> {
  const result = new Set<string>();
  for (const source of sources) {
    const keys = Object.keys(source.value ?? {});
    for (const key of keys) {
      result.add(key);
    }
  }
  return result;
}

function mergeRecursive(
  sources: PropsSource[],
  themeValue: AppExtendedThemeValue | undefined,
): MetadataMergeResult {
  const mergeType = determineMergeType(sources[0]?.value);
  if (mergeType !== MergeType.OTHER) {
    for (const source of sources) {
      if (determineMergeType(source?.value) === mergeType) {
        continue;
      }
      return mergeOthers(sources, themeValue);
    }
  }
  if (mergeType === MergeType.RECORD) {
    return mergeRecords(sources, themeValue);
  } else {
    return mergeOthers(sources, themeValue);
  }
}

function mergeRecords(
  sources: PropsSource[],
  themeValue: AppExtendedThemeValue | undefined,
): MetadataMergeResult {
  const result: MetadataMergeResult = {
    value: {},
    metadata: {},
  };
  const keys = getKeysToMerge(sources);
  let index = 0;
  let childSourceType: SourceType | null | undefined;

  for (const key of keys) {
    const keySources: PropsSource[] = [];

    for (const source of sources) {
      if (Object.prototype.hasOwnProperty.call(source.value, key)) {
        keySources.push({ value: (source.value as any)[key], source: source.source });
      }
    }

    const r = mergeRecursive(keySources, themeValue);
    result.value[key] = r.value;
    result.metadata[key] = r.metadata;

    if (index === 0) {
      childSourceType = r.metadata[sourceSymbol];
    } else if (childSourceType && childSourceType !== r.metadata[sourceSymbol]) {
      childSourceType = undefined;
    }

    index++;
  }

  result.metadata[previousSourceSymbol] = sources.at(-2)?.source;
  result.metadata[sourceSymbol] = sources.at(-1)?.source;

  return result;
}

function mergeOthers(
  sources: PropsSource[],
  themeValue: AppExtendedThemeValue | undefined,
): MetadataMergeResult {
  let lastSource = sources.at(-1);
  let preLastSource = sources.at(-2);

  const unsetDetected = lastSource?.value === UNSET_PROPERTY_KEY ? true : undefined;

  /**
   * Unset can be set only in element overrides.
   * If it is present, then use value previous element override.
   */
  if (unsetDetected) {
    lastSource = sources.at(-3);
    preLastSource = sources.at(-4);
  }

  if (lastSource === undefined) {
    return { value: undefined, metadata: {} };
  }

  const { value, themeValueKey } = resolveThemeValue(lastSource.value, themeValue);
  return {
    value: value,
    metadata: {
      [sourceSymbol]: lastSource.source,
      [previousSourceSymbol]: preLastSource?.source,
      [themeValueKeySymbol]: themeValueKey,
      [unsetDetectedSymbol]: unsetDetected,
    },
  };
}

function resolveThemeValue(
  value: unknown,
  themeValue: AppExtendedThemeValue | undefined,
): { value: unknown; themeValueKey?: keyof AppExtendedThemeValue } {
  if (!themeValue || typeof value !== 'string') {
    return { value };
  }

  if (!value.startsWith('$t.')) {
    return { value };
  }

  const themeValueKey = value.substring(3);
  if (themeValueKey.startsWith('palettes')) {
    return {
      value: getValueByPath(themeValue, themeValueKey),
      themeValueKey: value as keyof AppExtendedThemeValue,
    };
  }

  return {
    value: themeValue[themeValueKey as keyof AppExtendedThemeValue],
    themeValueKey: value as keyof AppExtendedThemeValue,
  };
}

export function mergePropsWithMetadata(
  sources: PropsSource[],
  themeValue?: AppExtendedThemeValue,
): MetadataMergeResult {
  return mergeRecursive(
    sources.map((s) => ({ ...s, value: s.value ?? {} })),
    themeValue,
  );
}
